import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { catalogApi } from "../../api/axios";

/**
 * Get items
 */
export const getItems = createAsyncThunk(
  "item/getItems",
  async ({ storeId, categoryId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products?store_id=${storeId}${
          categoryId ? `&category_id=${categoryId}` : ""
        }`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get units
 */
export const getUnits = createAsyncThunk(
  "item/getUnits",
  async ({ rejectWithValue }) => {
    try {
      const res = await catalogApi.get(`/v1/products/units/key-value`);
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get types
 */
export const getItemTypes = createAsyncThunk(
  "item/getItemTypes",
  async ({ storeId, rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/types/key-value?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/**
 * Get taxes
 */
export const getTaxes = createAsyncThunk(
  "item/getTaxes",
  async ({ storeId, rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/taxes/taxes-and-service-charges?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get Item Details
 */
export const getItemDetails = createAsyncThunk(
  "item/getItemDetails",
  async ({ storeId, itemUid }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/details?store_id=${storeId}&product_id=${itemUid}`
      );
      return res.data || {};
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/**
 * Add item
 */
export const addItem = createAsyncThunk(
  "item/addItem",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.post("/v1/products", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update item
 */
export const updateItem = createAsyncThunk(
  "item/updateItem",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.put("/v1/products", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete item
 */
export const itemDelete = createAsyncThunk(
  "item/itemDelete",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/actions", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return deleted payload to update state
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Perform publish action (e.g. activate/deactivate)
 */
export const itemPublishAction = createAsyncThunk(
  "item/itemPublishAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/published", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated item action info
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/**
 * Perform favorite action (e.g. mark/unmark favorite)
 */

export const itemFavoriteAction = createAsyncThunk(
  "item/itemFavoriteAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/favorite", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated item action info
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Slice
const itemSlice = createSlice({
  name: "items",
  initialState: {
    list: [],
    units: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearItemState: (state) => {
      state.list = [];
      state.units = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getItems.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(getItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ADD
      .addCase(addItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addItem.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(addItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // UPDATE
      .addCase(updateItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.list = state.list.map((i) => (i.id === updated.id ? updated : i));
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // DELETE
      .addCase(itemDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(itemDelete.fulfilled, (state, action) => {
        state.loading = false;
        const deleted = action.payload;
        state.list = state.list.filter(
          (i) => i.product_uid !== deleted.product_uid
        );
      })
      .addCase(itemDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // PUBLISH ACTION
      .addCase(itemPublishAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(itemPublishAction.fulfilled, (state, action) => {
        state.loading = false;
        const { product_uid, is_published } = action.payload;
        state.list = state.list.map((i) =>
          i.product_uid === product_uid ? { ...i, is_published } : i
        );
      })
      .addCase(itemPublishAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // FAVORITE ACTION
      .addCase(itemFavoriteAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(itemFavoriteAction.fulfilled, (state, action) => {
        state.loading = false;
        const { product_uid, is_favorite } = action.payload;
        state.list = state.list.map((i) =>
          i.product_uid === product_uid ? { ...i, is_favorite } : i
        );
      })
      .addCase(itemFavoriteAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // GET UNITS
      .addCase(getUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = action.payload;
      })
      .addCase(getUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearItemState } = itemSlice.actions;
export default itemSlice.reducer;
